<script setup lang="ts">
import type { AtsSection, AtsSectionState, AtsUploadReport } from '~/types/ats'
import type { CVData } from '~/types/cv'
import { forgetGuestCv, useTailorEntry } from '~/composables/useTailorEntry'
import { cvPromptText } from '~/utils/cvPromptText'
import { isPostingUrl } from '~/utils/postingUrl'

/**
 * ATS check of a CV: upload the PDF (or paste the text), optionally paste a
 * job posting, get the score and what the system cannot read. The
 * improvements are the pack's: a free report shows how many there are and
 * the checkout, never the improvements themselves (the server does not send
 * them).
 *
 * As in the tailoring tool (MarketingTailorTool), a CV made here can be
 * checked without a file (the account's, or the one made in this browser
 * without an account, useTailorEntry), and a visitor with no CV at all makes
 * one first in the homepage chat, in a dialog. Those go to the check as text:
 * the contact details, then the CV as the prompts read it (cvPromptText).
 */

type InputMode = 'mine' | 'pdf' | 'text' | 'chat'
type FailureCode = 'FILE_TOO_LARGE' | 'FILE_TYPE' | 'PDF_NO_TEXT' | 'PDF_UNREADABLE' | 'TOO_SHORT' | 'DAILY_LIMIT' | 'FAILED'

/** Same limits as the server (server/utils/atsText.ts, the endpoint). */
const MAX_FILE_BYTES = 4 * 1024 * 1024
const MAX_CV_TEXT = 12000
const MAX_POSTING = 8000
const MIN_CV_TEXT = 100

const SECTIONS: AtsSection[] = ['contact', 'summary', 'experience', 'education', 'skills']
const SECTION_ICON: Record<AtsSectionState, string> = {
  complete: 'bi-check-circle-fill text-accent',
  partial: 'bi-exclamation-circle-fill text-yellow-500',
  missing: 'bi-x-circle-fill text-red-500',
}

const { t, locale } = useI18n()
const { isAuthenticated } = useAuthUser()
const { isPremium, checkPremiumStatus } = usePremiumStatus()
const { trackUserAction } = useAnalytics()

const { accountCvs, guestCv, loadAccountCv } = useTailorEntry()
const hasOwnCvs = computed(() => accountCvs.value.length > 0 || (!isAuthenticated.value && !!guestCv.value))
const modes = computed(() => [...(hasOwnCvs.value ? ['mine' as const] : []), 'pdf' as const, 'text' as const, 'chat' as const])
const MODE_ICON: Record<InputMode, string> = {
  mine: 'bi-person-badge',
  pdf: 'bi-file-earmark-pdf',
  text: 'bi-textarea-t',
  chat: 'bi-chat-dots',
}
/** Picked by the visitor; until then, their own CVs when they have any. */
const pickedMode = ref<InputMode | null>(null)
const mode = computed<InputMode>(() => pickedMode.value ?? (hasOwnCvs.value ? 'mine' : 'pdf'))

function pickMode(value: InputMode) {
  pickedMode.value = value
  failure.value = null
  cvError.value = ''
}

/* A CV made here: picked from the visitor's own, or built in the chat. */
const ownCv = ref<CVData | null>(null)
const pickedOwn = ref<string | null>(null)
const loadingOwn = ref(false)
const chatCv = ref<CVData | null>(null)
const chatOpen = ref(false)
const cvError = ref('')

async function pickAccountCv(id: string) {
  pickedOwn.value = id
  ownCv.value = null
  cvError.value = ''
  loadingOwn.value = true
  const data = await loadAccountCv(id).catch(() => null)
  if (pickedOwn.value !== id) return
  loadingOwn.value = false
  if (data) ownCv.value = data
  else cvError.value = t('atsUpload.form.loadError')
}

async function pickGuestCv() {
  const saved = guestCv.value
  if (!saved) return
  pickedOwn.value = saved.id
  ownCv.value = null
  cvError.value = ''
  loadingOwn.value = true
  try {
    const response = await $fetch<{ cvData: CVData }>(`/api/guest-cv/${encodeURIComponent(saved.id)}`, { query: { sig: saved.sig } })
    if (pickedOwn.value === saved.id) ownCv.value = response.cvData
  } catch (err) {
    if (pickedOwn.value !== saved.id) return
    // Past its 30 days: not offered again.
    if ((err as { statusCode?: number }).statusCode === 404) {
      forgetGuestCv(saved.id)
      guestCv.value = null
      pickedOwn.value = null
      pickedMode.value = 'pdf'
      cvError.value = t('landing.builder.errors.draftExpired')
      return
    }
    cvError.value = t('atsUpload.form.loadError')
  } finally {
    if (pickedOwn.value === saved.id) loadingOwn.value = false
  }
}

function onChatBuilt(built: CVData) {
  chatCv.value = built
  chatOpen.value = false
  nextTick(() => document.getElementById('ats-chat-cv')?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
}

/** "Jana Nováková · Accountant", or a stand-in when the CV has neither. */
function cvLabel(item: { name?: string, title?: string }) {
  return [item.name?.trim(), item.title?.trim()].filter(Boolean).join(' · ') || t('atsUpload.form.untitled')
}

/** "12 Oct 2026", as the CV list writes it. */
function formatDate(value: Date | string) {
  return new Date(value).toLocaleDateString(locale.value === 'en' ? 'en-GB' : locale.value, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** When the remembered guest CV was made: it is kept for 30 days from then. */
const guestCvMade = computed(() => guestCv.value
  ? formatDate(new Date(new Date(guestCv.value.expiresAt).getTime() - 30 * 24 * 60 * 60 * 1000))
  : '')

/** The CV made here as the text the check reads: its contact lines first, which the prompt text leaves out. */
function siteCvText(cv: CVData): string {
  const contact = cv.contact
  const lines = [contact?.email, contact?.phone, contact?.location, contact?.linkedin, contact?.website, ...(cv.websites ?? []).map(site => site.url)]
    .map(value => value?.trim())
    .filter(Boolean)
  return [lines.join(' · '), cvPromptText(cv as unknown as Record<string, unknown>)].filter(Boolean).join('\n\n')
}

/** The CV made here that the check would read, for the "mine" and "chat" modes. */
const siteCv = computed(() => (mode.value === 'mine' ? ownCv.value : mode.value === 'chat' ? chatCv.value : null))
const file = ref<File | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const cvText = ref('')
const jobDescription = ref('')
const postingUrl = reactive(usePostingFromUrl(jobDescription, { max: MAX_POSTING }))

const isChecking = ref(false)
const failure = ref<FailureCode | null>(null)
const report = ref<AtsUploadReport | null>(null)
const resultsEl = ref<HTMLElement | null>(null)
const showCheckout = ref(false)
const checkoutOpened = ref(false)

const canSubmit = computed(() => {
  if (isChecking.value) return false
  if (mode.value === 'pdf') return !!file.value
  if (mode.value === 'text') return cvText.value.trim().length >= MIN_CV_TEXT
  return !!siteCv.value
})

/** What the free report left out: the pack's improvements, counted. */
const locked = computed(() => {
  const hidden = report.value?.hidden ?? {}
  return {
    suggestions: hidden.suggestions ?? 0,
    keywords: hidden.recommended_keywords ?? 0,
    changes: hidden.recommended_changes ?? 0,
    issues: hidden.issues ?? 0,
    missing: hidden.missing_skills ?? 0,
  }
})
const lockedTotal = computed(() => locked.value.suggestions + locked.value.keywords + locked.value.changes)

onMounted(() => {
  checkPremiumStatus()
})

function scoreClass(score: number) {
  if (score >= 80) return 'border-accent text-accent'
  if (score >= 60) return 'border-yellow-400 text-yellow-600 dark:text-yellow-400'
  if (score >= 40) return 'border-orange-400 text-orange-600 dark:text-orange-400'
  return 'border-red-500 text-red-600 dark:text-red-400'
}

function scoreVerdict(score: number) {
  if (score >= 80) return t('atsUpload.verdict.excellent')
  if (score >= 60) return t('atsUpload.verdict.good')
  if (score >= 40) return t('atsUpload.verdict.fair')
  return t('atsUpload.verdict.poor')
}

function pickFile(picked: File | null | undefined) {
  failure.value = null
  if (!picked) return
  const name = picked.name.toLowerCase()
  if (!name.endsWith('.pdf') && !name.endsWith('.txt')) {
    failure.value = 'FILE_TYPE'
    return
  }
  if (picked.size > MAX_FILE_BYTES) {
    failure.value = 'FILE_TOO_LARGE'
    return
  }
  file.value = picked
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  pickFile(input.files?.[0])
  input.value = ''
}

function onDrop(event: DragEvent) {
  isDragging.value = false
  pickFile(event.dataTransfer?.files?.[0])
}

function formatSize(bytes: number) {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} kB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

async function runCheck() {
  if (!canSubmit.value) return
  isChecking.value = true
  failure.value = null

  const body = new FormData()
  if (mode.value === 'pdf' && file.value) body.append('file', file.value)
  else if (siteCv.value) body.append('text', siteCvText(siteCv.value).slice(0, MAX_CV_TEXT))
  else body.append('text', cvText.value.trim())
  // A link that could not be read is no posting to compare with.
  if (jobDescription.value.trim() && !isPostingUrl(jobDescription.value)) body.append('jobDescription', jobDescription.value.trim())

  try {
    report.value = await $fetch<AtsUploadReport>('/api/ai/ats-upload-check', { method: 'POST', body })
    trackUserAction('ats_upload_check', {
      source: mode.value,
      with_posting: !!report.value.match,
      limited: report.value.limited,
      ats_score: report.value.ats_score,
    })
    await nextTick()
    resultsEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } catch (err) {
    const fetchError = err as { statusCode?: number; response?: { status?: number }; data?: { data?: { code?: string } } }
    const status = fetchError.statusCode ?? fetchError.response?.status
    const code = fetchError.data?.data?.code as FailureCode | undefined
    failure.value = status === 429 ? 'DAILY_LIMIT' : code ?? 'FAILED'
    if (failure.value === 'PDF_NO_TEXT') report.value = null
  } finally {
    isChecking.value = false
  }
}

function openCheckout() {
  trackUserAction('ats_upload_unlock_click', { locked: lockedTotal.value, logged_in: isAuthenticated.value })
  checkoutOpened.value = true
  showCheckout.value = true
}

/** The pack has landed: run the same CV again, this time for the full report. */
async function onPurchased() {
  showCheckout.value = false
  await checkPremiumStatus()
  await runCheck()
}
</script>

<template>
  <section id="check" class="sofax scroll-mt-24">
    <div class="section-space">
      <div class="shell">
        <SofaxSectionHeading
          :eyebrow="t('atsUpload.eyebrow')"
          :title="t('atsUpload.title')"
          :text="t('atsUpload.subtitle')"
        />

        <form class="card mx-auto max-w-3xl space-y-6" @submit.prevent="runCheck">
          <!-- 1. The CV -->
          <div>
            <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div class="display-heading display-heading-5">{{ t('atsUpload.form.cvHeading') }}</div>
              <div class="inline-flex flex-wrap rounded-[1.25rem] bg-surface p-1 dark:bg-gray-700" role="tablist">
                <button
                  v-for="option in modes"
                  :key="option"
                  type="button"
                  role="tab"
                  :aria-selected="mode === option"
                  class="rounded-full px-4 py-1.5 text-sm font-semibold transition"
                  :class="mode === option ? 'bg-white text-ink shadow-sm dark:bg-gray-900 dark:text-white' : 'text-muted hover:text-ink dark:text-gray-400 dark:hover:text-white'"
                  @click="pickMode(option)"
                >
                  <i class="bi mr-1.5" :class="MODE_ICON[option]" aria-hidden="true" />
                  {{ t(`atsUpload.form.mode.${option}`) }}
                </button>
              </div>
            </div>

            <!-- A CV made here: the account's, or the one made in this browser -->
            <div v-if="mode === 'mine'" class="space-y-2">
              <button
                v-for="item in accountCvs"
                :key="item.id"
                type="button"
                class="flex w-full items-center gap-3 rounded-[0.625rem] border-2 p-4 text-left transition-colors"
                :class="pickedOwn === item.id ? 'border-accent bg-accent-soft/20' : 'border-line hover:border-muted dark:border-gray-700'"
                :aria-pressed="pickedOwn === item.id"
                @click="pickAccountCv(item.id)"
              >
                <i class="bi text-lg" :class="pickedOwn === item.id ? 'bi-record-circle-fill text-accent' : 'bi-circle text-muted'" aria-hidden="true" />
                <span class="min-w-0 flex-1">
                  <span class="block truncate font-semibold text-ink dark:text-white">{{ cvLabel(item) }}</span>
                  <span class="block text-sm text-muted dark:text-gray-400">{{ t('atsUpload.form.made', { date: formatDate(item.createdAt) }) }}</span>
                </span>
                <span v-if="loadingOwn && pickedOwn === item.id" class="size-4 animate-spin rounded-full border-2 border-accent border-t-transparent" aria-hidden="true" />
              </button>
              <button
                v-if="!isAuthenticated && guestCv"
                type="button"
                class="flex w-full items-center gap-3 rounded-[0.625rem] border-2 p-4 text-left transition-colors"
                :class="pickedOwn === guestCv.id ? 'border-accent bg-accent-soft/20' : 'border-line hover:border-muted dark:border-gray-700'"
                :aria-pressed="pickedOwn === guestCv.id"
                @click="pickGuestCv"
              >
                <i class="bi text-lg" :class="pickedOwn === guestCv.id ? 'bi-record-circle-fill text-accent' : 'bi-circle text-muted'" aria-hidden="true" />
                <span class="min-w-0 flex-1">
                  <span class="block font-semibold text-ink dark:text-white">{{ t('atsUpload.form.guest') }}</span>
                  <span class="block text-sm text-muted dark:text-gray-400">{{ t('atsUpload.form.made', { date: guestCvMade }) }}</span>
                </span>
                <span v-if="loadingOwn && pickedOwn === guestCv.id" class="size-4 animate-spin rounded-full border-2 border-accent border-t-transparent" aria-hidden="true" />
              </button>
            </div>

            <!-- No CV yet: the homepage's chat, then the check -->
            <div v-else-if="mode === 'chat'" id="ats-chat-cv" class="scroll-mt-24">
              <div v-if="chatCv" class="flex items-center gap-4 rounded-[0.625rem] border border-accent bg-accent-soft/20 p-4">
                <i class="bi bi-check-circle-fill text-2xl text-accent" aria-hidden="true" />
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold text-ink dark:text-white">{{ cvLabel(chatCv) }}</div>
                  <div class="text-sm text-muted dark:text-gray-400">{{ t('atsUpload.form.chatReady') }}</div>
                </div>
                <button type="button" class="text-sm font-semibold text-muted underline-offset-4 hover:text-ink hover:underline dark:text-gray-400 dark:hover:text-white" @click="chatOpen = true">
                  {{ t('atsUpload.form.chatAgain') }}
                </button>
              </div>
              <div v-else class="rounded-[0.625rem] bg-surface p-5 dark:bg-gray-800">
                <div class="font-title text-lg font-bold text-ink dark:text-white">{{ t('atsUpload.form.chatTitle') }}</div>
                <p class="mt-1 !text-sm">{{ t('atsUpload.form.chatText') }}</p>
                <SofaxBtn variant="dark" class="mt-4" :label="t('atsUpload.form.chatCta')" @click="chatOpen = true" />
              </div>
            </div>

            <div v-else-if="mode === 'pdf'">
              <div
                v-if="!file"
                class="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-[0.625rem] border-2 border-dashed px-6 py-10 text-center transition"
                :class="isDragging ? 'border-accent bg-accent-soft/20' : 'border-line hover:border-accent dark:border-gray-600'"
                role="button"
                tabindex="0"
                @click="fileInput?.click()"
                @keydown.enter.prevent="fileInput?.click()"
                @keydown.space.prevent="fileInput?.click()"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="onDrop"
              >
                <i class="bi bi-cloud-arrow-up text-4xl text-accent" aria-hidden="true" />
                <div class="font-semibold text-ink dark:text-white">{{ t('atsUpload.form.drop') }}</div>
                <p class="!text-sm text-muted dark:text-gray-400">{{ t('atsUpload.form.dropHint') }}</p>
              </div>
              <div v-else class="flex items-center gap-4 rounded-[0.625rem] border border-line p-4 dark:border-gray-700">
                <i class="bi bi-file-earmark-pdf-fill text-3xl text-red-500" aria-hidden="true" />
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold text-ink dark:text-white">{{ file.name }}</div>
                  <div class="text-sm text-muted dark:text-gray-400">{{ formatSize(file.size) }}</div>
                </div>
                <button type="button" class="text-sm font-semibold text-muted underline-offset-4 hover:text-ink hover:underline dark:text-gray-400 dark:hover:text-white" @click="file = null">
                  {{ t('atsUpload.form.removeFile') }}
                </button>
              </div>
              <input ref="fileInput" type="file" accept=".pdf,application/pdf,.txt,text/plain" class="sr-only" @change="onFileChange">
              <!-- No file to hand: the chat makes the CV first -->
              <p v-if="!file" class="mt-3 text-center !text-sm text-muted dark:text-gray-400">
                <button type="button" class="link" @click="pickMode('chat'); chatOpen = true">
                  <i class="bi bi-chat-dots mr-1" aria-hidden="true" />{{ t('atsUpload.form.noCv') }}
                </button>
              </p>
            </div>

            <SofaxTextarea
              v-else
              v-model="cvText"
              :rows="10"
              :maxlength="MAX_CV_TEXT"
              :placeholder="t('atsUpload.form.textPlaceholder')"
              :hint="`${cvText.length}/${MAX_CV_TEXT}`"
            />
          </div>

          <!-- 2. The posting (optional) -->
          <div>
            <div class="display-heading display-heading-5 mb-1">{{ t('atsUpload.form.postingHeading') }}</div>
            <p class="mb-3 !text-sm text-muted dark:text-gray-400">{{ t('atsUpload.form.postingHint') }}</p>
            <SofaxTextarea
              v-model="jobDescription"
              :rows="5"
              :maxlength="MAX_POSTING"
              :placeholder="t('atsUpload.form.postingPlaceholder')"
            />
            <CommonPostingUrlStatus :is-loading="postingUrl.isLoading" :error="postingUrl.error" :loaded-from="postingUrl.loadedFrom" />
          </div>

          <SofaxNotice v-if="cvError" type="error">{{ cvError }}</SofaxNotice>

          <SofaxNotice v-if="failure" :type="failure === 'PDF_NO_TEXT' ? 'warning' : 'error'">
            <div class="font-semibold">{{ t(`atsUpload.error.${failure}.title`) }}</div>
            <p class="!text-sm">{{ t(`atsUpload.error.${failure}.text`) }}</p>
            <div v-if="failure === 'PDF_NO_TEXT'" class="mt-2">
              <button type="button" class="text-sm font-semibold underline underline-offset-4" @click="pickMode('text')">
                {{ t('atsUpload.error.PDF_NO_TEXT.action') }}
              </button>
            </div>
            <div v-if="failure === 'DAILY_LIMIT'" class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <LocaleLink v-if="!isAuthenticated" to="/registration" class="font-semibold underline underline-offset-4">
                {{ t('atsUpload.error.DAILY_LIMIT.register') }}
              </LocaleLink>
              <button type="button" class="font-semibold underline underline-offset-4" @click="openCheckout">
                {{ t('atsUpload.error.DAILY_LIMIT.buy') }}
              </button>
            </div>
          </SofaxNotice>

          <div class="flex flex-col items-center gap-3">
            <SofaxBtn
              type="submit"
              :label="isChecking ? t('atsUpload.form.checking') : t('atsUpload.form.submit')"
              :disabled="!canSubmit"
            />
            <p class="!text-sm text-muted dark:text-gray-400">{{ t(isPremium ? 'atsUpload.form.notePremium' : 'atsUpload.form.noteFree') }}</p>
          </div>
        </form>

        <!-- Results -->
        <div v-if="report" ref="resultsEl" class="mx-auto mt-10 max-w-3xl scroll-mt-24 space-y-6" aria-live="polite">
          <div class="card">
            <div class="grid gap-6" :class="report.match ? 'sm:grid-cols-2' : ''">
              <div class="flex flex-col items-center text-center">
                <div class="flex size-28 items-center justify-center rounded-full border-8" :class="scoreClass(report.ats_score)">
                  <span class="font-title text-3xl font-bold">{{ report.ats_score }}%</span>
                </div>
                <div class="mt-3 font-semibold text-ink dark:text-white">{{ t('atsUpload.result.atsScore') }}</div>
                <p class="!text-sm text-muted dark:text-gray-400">{{ scoreVerdict(report.ats_score) }}</p>
              </div>
              <div v-if="report.match" class="flex flex-col items-center text-center">
                <div class="flex size-28 items-center justify-center rounded-full border-8" :class="scoreClass(report.match.match_score)">
                  <span class="font-title text-3xl font-bold">{{ report.match.match_score }}%</span>
                </div>
                <div class="mt-3 font-semibold text-ink dark:text-white">{{ t('atsUpload.result.matchScore') }}</div>
                <p class="!text-sm text-muted dark:text-gray-400">{{ t('atsUpload.result.matchHint') }}</p>
              </div>
            </div>
            <div class="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-muted dark:text-gray-400">
              <span v-if="report.detected_title"><i class="bi bi-person-badge mr-1" aria-hidden="true" />{{ report.detected_title }}</span>
              <span><i class="bi bi-fonts mr-1" aria-hidden="true" />{{ t('atsUpload.result.words', { count: report.words }) }}</span>
              <span v-if="report.pages"><i class="bi bi-files mr-1" aria-hidden="true" />{{ t('atsUpload.result.pages', { count: report.pages }) }}</span>
            </div>
          </div>

          <!-- What the parser got out of the file: objective, free -->
          <div class="card">
            <div class="display-heading display-heading-5 mb-4">{{ t('atsUpload.result.sections') }}</div>
            <ul class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <li v-for="section in SECTIONS" :key="section" class="flex items-center gap-3">
                <i class="bi text-xl" :class="SECTION_ICON[report.sections[section]]" aria-hidden="true" />
                <span class="font-semibold text-ink dark:text-white">{{ t(`atsUpload.section.${section}`) }}</span>
                <span class="text-sm text-muted dark:text-gray-400">{{ t(`atsUpload.state.${report.sections[section]}`) }}</span>
              </li>
            </ul>
            <SofaxNotice v-if="report.text_risks.length" type="warning" class="mt-5">
              <div class="mb-1 font-semibold">{{ t('atsUpload.result.textRisks') }}</div>
              <ul class="list-disc space-y-1 pl-5 text-sm">
                <li v-for="risk in report.text_risks" :key="risk">{{ t(`atsUpload.risk.${risk}`) }}</li>
              </ul>
            </SofaxNotice>
            <SofaxNotice v-else type="success" class="mt-5">
              {{ t('atsUpload.result.noTextRisks') }}
            </SofaxNotice>
          </div>

          <div v-if="report.issues.length" class="card">
            <div class="display-heading display-heading-5 mb-4">{{ t('atsUpload.result.issues') }}</div>
            <ul class="space-y-3">
              <li v-for="(issue, index) in report.issues" :key="index" class="flex items-start gap-3">
                <i class="bi bi-x-circle mt-0.5 shrink-0 text-xl text-red-500" aria-hidden="true" />
                <span>{{ issue }}</span>
              </li>
            </ul>
            <p v-if="locked.issues" class="mt-4 !text-sm font-semibold text-muted dark:text-gray-400">
              <i class="bi bi-lock-fill mr-1" aria-hidden="true" />{{ t('atsUpload.result.moreIssues', { count: locked.issues }) }}
            </p>
          </div>

          <div v-if="report.match" class="card">
            <div class="display-heading display-heading-5 mb-4">{{ t('atsUpload.result.posting') }}</div>
            <div v-if="report.match.matching_skills.length" class="mb-5">
              <div class="mb-2 text-sm font-semibold text-ink dark:text-white">{{ t('atsUpload.result.matching') }}</div>
              <div class="flex flex-wrap gap-2">
                <span v-for="skill in report.match.matching_skills" :key="skill" class="rounded-full bg-accent-soft/30 px-3 py-1 text-sm text-ink dark:bg-accent/20 dark:text-white">
                  <i class="bi bi-check2 mr-1" aria-hidden="true" />{{ skill }}
                </span>
              </div>
            </div>
            <div v-if="report.match.missing_skills.length">
              <div class="mb-2 text-sm font-semibold text-ink dark:text-white">{{ t('atsUpload.result.missing') }}</div>
              <div class="flex flex-wrap gap-2">
                <span v-for="skill in report.match.missing_skills" :key="skill" class="rounded-full bg-red-50 px-3 py-1 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  <i class="bi bi-dash mr-1" aria-hidden="true" />{{ skill }}
                </span>
                <span v-if="locked.missing" class="rounded-full border border-dashed border-line px-3 py-1 text-sm text-muted dark:border-gray-600 dark:text-gray-400">
                  <i class="bi bi-lock-fill mr-1" aria-hidden="true" />{{ t('atsUpload.result.moreMissing', { count: locked.missing }) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Improvements: the pack's -->
          <div v-if="report.limited" class="card relative overflow-hidden border-accent">
            <div class="pointer-events-none select-none space-y-3 blur-sm" aria-hidden="true">
              <div class="display-heading display-heading-5">{{ t('atsUpload.result.improvements') }}</div>
              <div v-for="n in 4" :key="n" class="h-4 rounded bg-surface dark:bg-gray-700" :style="{ width: `${95 - n * 12}%` }" />
              <div class="flex flex-wrap gap-2 pt-2">
                <span v-for="n in 6" :key="n" class="h-7 w-20 rounded-full bg-accent-soft/40" />
              </div>
            </div>
            <div class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/70 p-6 text-center dark:bg-gray-900/70">
              <i class="bi bi-lock-fill text-3xl text-accent" aria-hidden="true" />
              <div class="display-heading display-heading-5">{{ t('atsUpload.locked.title') }}</div>
              <ul class="flex flex-wrap justify-center gap-x-5 gap-y-1 text-sm text-ink dark:text-white">
                <li v-if="locked.suggestions">{{ t('atsUpload.locked.suggestions', { count: locked.suggestions }) }}</li>
                <li v-if="locked.keywords">{{ t('atsUpload.locked.keywords', { count: locked.keywords }) }}</li>
                <li v-if="locked.changes">{{ t('atsUpload.locked.changes', { count: locked.changes }) }}</li>
              </ul>
              <SofaxBtn :label="t('atsUpload.locked.cta')" @click="openCheckout" />
            </div>
          </div>

          <template v-else>
            <div v-if="report.suggestions.length" class="card">
              <div class="display-heading display-heading-5 mb-4">{{ t('atsUpload.result.improvements') }}</div>
              <ol class="list-decimal space-y-2 pl-5">
                <li v-for="(suggestion, index) in report.suggestions" :key="index">{{ suggestion }}</li>
              </ol>
            </div>
            <div v-if="report.recommended_keywords.length" class="card">
              <div class="display-heading display-heading-5 mb-4">{{ t('atsUpload.result.keywords') }}</div>
              <div class="flex flex-wrap gap-2">
                <span v-for="keyword in report.recommended_keywords" :key="keyword" class="rounded-full border border-accent px-3 py-1 text-sm text-ink dark:text-white">
                  {{ keyword }}
                </span>
              </div>
            </div>
            <div v-if="report.match?.recommended_changes.length" class="card">
              <div class="display-heading display-heading-5 mb-4">{{ t('atsUpload.result.changes') }}</div>
              <ol class="list-decimal space-y-2 pl-5">
                <li v-for="(change, index) in report.match.recommended_changes" :key="index">{{ change }}</li>
              </ol>
            </div>
          </template>

          <div class="text-center">
            <SofaxBtn variant="outline" to="/cv/new" :label="t('atsUpload.result.rebuild')" />
          </div>
        </div>
      </div>
    </div>

    <LandingChatCvDialog
      v-model="chatOpen"
      :title="t('atsUpload.form.chatTitle')"
      :intro="t('atsUpload.form.chatText')"
      source="ats-check-chat"
      @built="onChatBuilt"
    />

    <!-- Loaded on the first click only: most visitors of this page never open it -->
    <LazyPremiumCheckoutModal v-if="checkoutOpened" v-model="showCheckout" lead="ats" @purchased="onPurchased" />
  </section>
</template>
